/**
 * @file CarrierPickupCacheCard.tsx
 * @description Блок «Кэш ПВЗ» в настройках перевозчика (phase19.12).
 *
 * Показывает свежесть локальной базы пунктов выдачи, даёт настроить область
 * синхронизации и период автообновления (сохраняются вместе с формой страницы),
 * запускает фоновый синк и ведёт живой журнал последних 10 запусков.
 *
 * Пока в журнале есть запись RUNNING — поллинг раз в 3 с; количество точек и
 * дату последнего синка берём из свежей записи журнала, а не из локального
 * счётчика (один источник правды).
 */

import { Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { CarrierPickupScopeFields } from "./CarrierPickupScopeFields";
import { CarrierSyncIntervalSelect } from "./CarrierSyncIntervalSelect";
import { CarrierSyncLogList } from "./CarrierSyncLogList";
import { Button } from "../ui/button";
import { useNotify } from "../ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import {
  fetchCarrierSyncLog,
  syncCarrierPickupPoints,
  type CarrierKind,
  type CarrierSyncLogEntry,
  type PickupSyncScope,
} from "../../lib/api-shop-carriers";

/** Интервал поллинга журнала, пока синк в статусе RUNNING. */
const POLL_INTERVAL_MS = 3000;

interface Props {
  carrier: CarrierKind;
  /** Точек в кэше по данным конфига (пока журнал не принёс свежие). */
  pickupPointCount: number;
  /** ISO даты последнего синка из конфига. */
  lastPickupSync: string | null;
  scope: PickupSyncScope;
  intervalHours: number | null;
  onChange: (patch: {
    pickupSyncScope?: PickupSyncScope;
    pickupSyncIntervalHours?: number | null;
  }) => void;
}

export function CarrierPickupCacheCard({
  carrier,
  pickupPointCount,
  lastPickupSync,
  scope,
  intervalHours,
  onChange,
}: Props) {
  const notify = useNotify();
  const [entries, setEntries] = useState<CarrierSyncLogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(true);
  const [logError, setLogError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const aliveRef = useRef(true);

  const loadLog = useCallback(async (): Promise<void> => {
    try {
      const data = await fetchCarrierSyncLog(carrier, 10);
      if (!aliveRef.current) return;
      setEntries([...data].sort((a, b) => b.startedAt.localeCompare(a.startedAt)));
      setLogError(null);
    } catch (err) {
      if (!aliveRef.current) return;
      setLogError(err instanceof ApiError ? err.message : "Журнал синхронизаций недоступен");
    } finally {
      if (aliveRef.current) setLogLoading(false);
    }
  }, [carrier]);

  useEffect(() => {
    aliveRef.current = true;
    void loadLog();
    return () => {
      aliveRef.current = false;
    };
  }, [loadLog]);

  const running = entries.some((e) => e.status === "RUNNING");

  // Поллинг живёт только пока синк идёт; уход со страницы снимает таймер.
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => void loadLog(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [running, loadLog]);

  // Свежая успешная запись журнала — актуальнее конфига, загруженного при входе.
  const lastSuccess = entries.find((e) => e.status === "SUCCESS" && e.finishedAt);
  const totalCount = lastSuccess ? lastSuccess.totalAfter : pickupPointCount;
  const lastSyncIso = lastSuccess?.finishedAt ?? lastPickupSync;
  const lastSyncLabel = lastSyncIso
    ? new Date(lastSyncIso).toLocaleString("ru-RU")
    : "никогда";

  const emptyScopeReason =
    scope.mode === "COUNTRIES" && (scope.countries ?? []).length === 0
      ? "Отметьте хотя бы одну страну"
      : scope.mode === "CITIES" && (scope.cities ?? []).length === 0
        ? "Добавьте хотя бы один населённый пункт"
        : null;

  async function onSync(): Promise<void> {
    setStarting(true);
    try {
      const res = await syncCarrierPickupPoints(carrier);
      if (res.alreadyRunning) notify.info("Синхронизация уже идёт");
      else notify.success(res.mock ? "Синхронизация запущена (mock-режим)" : "Синхронизация запущена");
      await loadLog();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось запустить синхронизацию");
    } finally {
      if (aliveRef.current) setStarting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 rounded-md border border-on-surface/10 bg-surface-low p-4">
      <div>
        <h4 className="text-sm font-medium">Кэш ПВЗ</h4>
        <p className="text-xs text-on-surface-variant">
          Точек в кэше: <strong>{totalCount.toLocaleString("ru-RU")}</strong> · последняя
          синхронизация: <strong>{lastSyncLabel}</strong>
        </p>
      </div>

      <CarrierPickupScopeFields
        carrier={carrier}
        value={scope}
        onChange={(next) => onChange({ pickupSyncScope: next })}
      />

      <CarrierSyncIntervalSelect
        value={intervalHours}
        onChange={(hours) => onChange({ pickupSyncIntervalHours: hours })}
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Button
          type="button"
          variant="outline"
          className="min-h-11 w-full sm:min-h-9 sm:w-auto"
          title={emptyScopeReason ?? undefined}
          disabled={starting || running || Boolean(emptyScopeReason)}
          onClick={() => void onSync()}
        >
          {starting || running ? (
            <Loader2 className="mr-1.5 size-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-1.5 size-4" />
          )}
          Синхронизировать сейчас
        </Button>
        <span className="text-xs text-on-surface-variant">
          {emptyScopeReason ?? (running ? "выполняется…" : "запуск в фоне, можно уйти со страницы")}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
          История (последние 10)
        </div>
        <CarrierSyncLogList entries={entries} loading={logLoading} error={logError} />
      </div>
    </div>
  );
}
